import React from "react";
import {Tooltip} from "@mui/material";
import {SwapHorizOutlined} from "@mui/icons-material";
import type {ClMatchBasis, ClTxChannel} from "../contractLinkageData";
import type {ClReviewTx, ClStatus} from "../contractLinkageUtils";
import {BASIS_HELP, BASIS_LABEL, CHANNEL_HELP, CHANNEL_LABEL, STATUS_LABEL, statusColors, tierColors, tierOfConfidence} from "../contractLinkageUtils";

export const ConfidencePill: React.FC<{confidence: number}> = ({confidence}) => {
    const {bg, fg} = tierColors(tierOfConfidence(confidence));
    return (
        <span className="cl-confidence-pill" style={{backgroundColor: bg, color: fg}}>
            <span className="cl-confidence-bar">
                <span style={{width: `${confidence}%`, backgroundColor: fg}}/>
            </span>
            {confidence}%
        </span>
    );
};

export const StatusChip: React.FC<{status: ClStatus}> = ({status}) => {
    const {bg, fg} = statusColors(status);
    return <span className="cl-status-chip" style={{backgroundColor: bg, color: fg}}>{STATUS_LABEL[status]}</span>;
};

export const BasisChip: React.FC<{basis: ClMatchBasis}> = ({basis}) => (
    <Tooltip title={BASIS_HELP[basis]} arrow>
        <span className="cl-basis-chip">{BASIS_LABEL[basis]}</span>
    </Tooltip>
);

/** Marks spend that reached the company indirectly (marketplace, payment provider, employee expense). */
export const ChannelChip: React.FC<{channel: ClTxChannel}> = ({channel}) => (
    <Tooltip title={CHANNEL_HELP[channel]} arrow>
        <span className={`cl-channel-chip is-${channel}`}>{CHANNEL_LABEL[channel]}</span>
    </Tooltip>
);

/** Whether a transaction date falls inside the selected contract's validity. */
export const PeriodFit: React.FC<{inPeriod: boolean}> = ({inPeriod}) => (
    <span className={`cl-periodfit${inPeriod ? ' is-in' : ' is-out'}`}>
        {inPeriod ? 'In period' : 'Outside period'}
    </span>
);

/** The effective contract link, with the original AI suggestion when the user relinked. */
export const ContractLinkCell: React.FC<{tx: ClReviewTx}> = ({tx}) => {
    if (!tx.effectiveContractId) {
        return <span className="cl-nolink">{tx.status === 'unlinked' ? 'Link removed' : 'No contract'}</span>;
    }
    const relinked = tx.status === 'relinked' && tx.effectiveContractId !== tx.aiContractId;
    const aiName = tx.aiContractName;
    return (
        <span className="cl-contract-cell">
            <span className="cl-contract-name">
                {relinked && <SwapHorizOutlined fontSize="inherit" className="cl-relink-icon"/>}
                {tx.effectiveContractName}
            </span>
            {relinked && <span className="cl-contract-was">AI suggested: {aiName ?? 'no contract'}</span>}
        </span>
    );
};
